import { useEffect, useState, useSyncExternalStore } from 'react';
import { Button, Group, Loader, Paper, SegmentedControl, Stack, Text } from '@mantine/core';
import { BarChart, getFilteredChartTooltipPayload } from '@mantine/charts';
import '@mantine/charts/styles.css';
import { WidgetFrame, type WidgetProps } from '@ayocore/platform/client/widgets';
import { useAuthController } from '@ayocore/platform/identity/client/useAuthController';
import { useUserSetting } from '@ayocore/platform/identity/client/settings/useUserSetting';
import { getSalesTrend } from '../../../client/rpc';
import { crmWidgetStyle } from '../../../client/components/Widgets/shared/presentation';
import { DEFAULT_SALES_TREND_SETTINGS, decodeSalesTrendSettings } from '../common/settings';
import { SalesTrendWidgetController } from './SalesTrendWidgetController';

const monthFormatter = new Intl.DateTimeFormat('ru-RU', {
  month: 'short',
  year: '2-digit',
  timeZone: 'UTC',
});
const formatDate = (date: string) => date.split('-').reverse().join('.');

/** Два ряда столбиков за шесть месяцев; область данных сохраняется в профиле. */
export function SalesTrendWidget(props: WidgetProps) {
  const userId = useAuthController(state => state.user?.id);
  return userId == null ? null : <UserSalesTrend key={userId} {...props} />;
}

function UserSalesTrend({ title = 'Продажи', className }: WidgetProps) {
  const preferences = useUserSetting('crm.widgets.sales-trend', {
    defaultValue: DEFAULT_SALES_TREND_SETTINGS,
    decode: decodeSalesTrendSettings,
  });
  const [controller] = useState(() => new SalesTrendWidgetController(getSalesTrend));
  const { data, loading, error } = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot
  );
  useEffect(() => {
    if (preferences.ready) void controller.load(preferences.value);
    return () => controller.cancelLoad();
  }, [controller, preferences.ready, preferences.value]);

  return (
    <WidgetFrame
      title={title}
      className={className}
      radius='lg'
      style={{ ...crmWidgetStyle, height: '100%', minWidth: 0 }}
    >
      <SegmentedControl
        size='xs'
        aria-label='Область данных продаж'
        value={preferences.value.scope}
        disabled={!preferences.ready || preferences.saving}
        onChange={scope => {
          if (scope === 'mine' || scope === 'branch' || scope === 'all')
            void preferences.setValue({ scope });
        }}
        data={[
          { label: 'Мои', value: 'mine' },
          { label: 'Филиал', value: 'branch' },
          { label: 'Все', value: 'all' },
        ]}
      />
      {preferences.error && (
        <Stack gap='xs'>
          <Text role='alert' c='red' size='sm'>
            {preferences.error}
          </Text>
          {!preferences.ready && (
            <Button size='xs' variant='light' onClick={() => void preferences.reload()}>
              Повторить загрузку настроек продаж
            </Button>
          )}
        </Stack>
      )}
      {(preferences.loading || (preferences.ready && loading)) && (
        <Group gap='xs' role='status'>
          <Loader size='xs' />
          <Text size='sm'>Загрузка продаж…</Text>
        </Group>
      )}
      {error && (
        <Stack gap='xs'>
          <Text role='alert' c='red' size='sm'>
            {error}
          </Text>
          <Button size='xs' variant='light' onClick={() => void controller.load(preferences.value)}>
            Повторить загрузку продаж
          </Button>
        </Stack>
      )}
      {data && (
        <>
          {data.documentCount === 0 && (
            <Text size='sm' c='dimmed'>
              За последние 6 месяцев нет отгрузок и поступлений.
            </Text>
          )}
          <BarChart
            h={230}
            data={data.points.map(point => ({
              ...point,
              month: monthFormatter.format(new Date(`${point.month}-01T00:00:00Z`)),
            }))}
            dataKey='month'
            withLegend
            series={[
              { name: 'shipments', label: 'Отгрузки', color: 'blue.6' },
              { name: 'receipts', label: 'Поступления', color: 'teal.6' },
            ]}
            valueFormatter={value =>
              `${new Intl.NumberFormat('ru-RU', { notation: 'compact', maximumFractionDigits: 1 }).format(value)} ${data.currency.symbol}`
            }
            tooltipProps={{
              content: ({ label, payload }) => (
                <Paper p='sm' withBorder radius='md' shadow='md'>
                  <Text size='sm' fw={500}>
                    {label}
                  </Text>
                  {getFilteredChartTooltipPayload([...(payload ?? [])]).map(item => (
                    <Text key={item.name} size='sm' c={item.color}>
                      {item.name === 'shipments' ? 'Отгрузки' : 'Поступления'}:{' '}
                      {new Intl.NumberFormat('ru-RU', {
                        maximumFractionDigits: data.currency.precision,
                      }).format(Number(item.value))}{' '}
                      {data.currency.symbol}
                    </Text>
                  ))}
                </Paper>
              ),
            }}
            yAxisProps={{ width: 72, tick: { fontSize: 10 } }}
            xAxisProps={{ tick: { fontSize: 11 } }}
            barProps={{ radius: [4, 4, 0, 0], isAnimationActive: false }}
          />
          <Text size='xs' c='dimmed'>
            {formatDate(data.period.from)} — {formatDate(data.period.to)}
          </Text>
        </>
      )}
    </WidgetFrame>
  );
}
